import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useAuth, type Role } from "@/auth/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15; // 30 seconds total

export function RequireRole({
  role,
  children,
}: {
  role: Role | Role[];
  children: React.ReactNode;
}) {
  const { user, loading, claim, refreshClaims } = useAuth();
  const roles = Array.isArray(role) ? role : [role];
  const [polling, setPolling] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const pollCountRef = useRef(0);

  // Auto-poll for claims when user is authenticated but claim is missing
  useEffect(() => {
    if (loading || !user || claim || pollExhausted) return;

    setPolling(true);
    pollCountRef.current = 0;

    const interval = setInterval(async () => {
      pollCountRef.current++;
      const result = await refreshClaims();

      if (result) {
        // Claim found, stop polling
        setPolling(false);
        clearInterval(interval);
      } else if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        // Give up after max attempts
        setPolling(false);
        setPollExhausted(true);
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loading, user, claim, pollExhausted, refreshClaims]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  // Show loading state while polling for claims
  if (!claim && polling) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-500" />
          <p className="text-sm text-muted-foreground">
            Setting up your account...
          </p>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="mx-auto max-w-md p-6">
        <Card>
          <CardHeader>
            <CardTitle>Account not set up</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>Your account hasn't been linked to an organization yet.</p>
            <p>
              Please complete the onboarding process or contact your
              administrator for access.
            </p>
            <div className="flex gap-2">
              <Button asChild>
                <a href="/onboarding">Set up account</a>
              </Button>
              <Button variant="outline" asChild>
                <a href="/login">Back</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!roles.includes(claim.role)) {
    return (
      <div className="mx-auto max-w-md p-6">
        <Card>
          <CardHeader>
            <CardTitle>Access denied</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>You don't have permission to access this area.</p>
            <Button asChild>
              <a href="/">Home</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
